/* =====================================================
   ui-security-reactive · bm-requires
   Habilita una acción solo si el usuario conectado tiene el permiso.

   Usage:
     <md-button bm-requires="BROWSE_RESOURCES" ng-click="...">…</md-button>
     <md-button bm-requires="EDIT_ROLE, EDIT_ANY_ROLE">…</md-button>            (alcanza con uno)
     <md-button bm-requires="VIEW_PROFILE, VIEW_ANY_PROFILE"
                bm-requires-all="VIEW_TEMPLATE">…</md-button>                     (y además todos estos)
     <md-button bm-requires="{{ transitionPermission(tr.target) }}">…</md-button> (dinámico: key o lista)
     <md-button bm-requires="DELETE_PROFILE" ng-disabled="!vm.selected">…</md-button>
     <div bm-requires="EDIT_PROFILE" bm-requires-mode="hide">…</div>

   - Sin permiso: el elemento queda deshabilitado (o oculto con mode="hide") y
     muestra el tooltip "Requires <permiso> permission" (en lugar del md-tooltip
     propio del elemento, si tiene uno). El tooltip se crea en el primer hover,
     para que md-tooltip se enganche al elemento y no a un ancestro (ej. dentro
     de un md-menu cerrado se enganchaba al botón del menú).
   - Botones y demás elementos quedan con aria-disabled + clase
     bm-requires-denied (estilo en ui-security-reactive.css) y el click
     bloqueado, así el tooltip se ve al pasar el mouse. Los controles de
     formulario (md-switch, md-checkbox, input…) usan disabled real, combinado
     con su ng-disabled si lo tienen.
   - Reactivo: se reevalúa en cada digest contra PermissionService, que se
     refresca solo (diálogos, rutas, foco de la ventana).
   - Acepta KEYs de PERMISSIONS o IDs de permiso. Un valor dinámico vacío
     (null/'') no exige nada.
   ===================================================== */
angular.module('bizmetryApp').directive('bmRequires', ['$compile',
  function ($compile) {

    // 'A, B' → "['A','B']"  |  '{{ expr }}' → "(expr)"
    function toExpr(value) {
      const dynamic = /^\s*\{\{([\s\S]*)\}\}\s*$/.exec(value || '');
      if (dynamic) return '(' + dynamic[1] + ')';
      const list = (value || '').split(',').map(function (s) { return s.trim(); }).filter(Boolean);
      return JSON.stringify(list).replace(/"/g, "'");
    }

    // Controles de formulario: ahí sí usamos `disabled` real (su estilo y su
    // manejo de gestos dependen de él). En botones y demás elementos NO: un
    // <button disabled> no dispara eventos de mouse y el tooltip nunca aparece,
    // así que se marcan con aria-disabled + clase y el click se bloquea a mano.
    const FORM_CONTROLS = ['MD-SWITCH', 'MD-CHECKBOX', 'MD-SELECT', 'INPUT', 'SELECT', 'TEXTAREA'];

    return {
      restrict: 'A',
      priority: 101,   // antes que ngDisabled (100): reescribimos su expresión
      compile: function (tElement, tAttrs) {
        const anyExpr = toExpr(tAttrs.bmRequires);
        const allExpr = tAttrs.bmRequiresAll ? toExpr(tAttrs.bmRequiresAll) : '[]';
        const allowedExpr = '$root.canSecure(' + anyExpr + ', ' + allExpr + ')';
        const hide = tAttrs.bmRequiresMode === 'hide';
        const formControl = FORM_CONTROLS.indexOf(tElement[0].tagName) !== -1;

        if (!hide) {
          if (formControl && tAttrs.ngDisabled) {
            tAttrs.$set('ngDisabled', '!' + allowedExpr + ' || (' + tAttrs.ngDisabled + ')');
          }
          // El tooltip propio del elemento se oculta mientras falte el permiso.
          angular.forEach(tElement.children(), function (child) {
            if (child.tagName !== 'MD-TOOLTIP') return;
            const own = angular.element(child);
            own.attr('ng-if', allowedExpr + (own.attr('ng-if') ? ' && (' + own.attr('ng-if') + ')' : ''));
          });
        }
        const tooltipText = '{{ $root.requiresTooltip(' + anyExpr + ', ' + allExpr + ') }}';

        return function link(scope, element, attrs) {
          const node = element[0];
          const ownsDisabled = !hide && formControl && !attrs.ngDisabled;
          // Fail closed: an expression error (e.g. a missing helper) denies instead of
          // leaving the action enabled.
          const allowed = function () {
            try { return scope.$eval(allowedExpr) === true; }
            catch (e) { console.warn('🔐 [bm-requires] ' + allowedExpr + ':', e); return false; }
          };

          // "Requires …" tooltip, created on the first hover while denied.
          // md-tooltip binds to the nearest parent with pointer events *when it
          // links*: linked up front inside a closed md-menu it would attach to
          // the menu trigger instead of the item. Creating it on hover, with the
          // element visible, binds it to the element itself.
          let tipScope = null;
          function removeTip() {
            if (!tipScope) return;
            tipScope.$destroy();   // md-tooltip removes its panel and listeners
            tipScope = null;
            if ((attrs.ariaLabel || '').indexOf('Requires ') === 0) attrs.$set('aria-label', undefined);
          }
          function showTip() {
            if (hide || allowed()) return;
            if (!tipScope) {
              tipScope = scope.$new();
              const tip = angular.element('<md-tooltip md-direction="top" md-autohide></md-tooltip>').text(tooltipText);
              element.append(tip);
              $compile(tip)(tipScope);
              if (!scope.$root.$$phase) scope.$apply();   // interpolate the text before md-tooltip shows it
            }
            element.triggerHandler('mouseenter');   // the tooltip's own handler was bound after this event
          }
          node.addEventListener('mouseenter', function () {
            if (!tipScope) showTip();
          });
          scope.$on('$destroy', removeTip);

          scope.$watch(allowed, function (ok) {
            if (ok) removeTip();
            if (hide) {
              element.css('display', ok ? '' : 'none');
            } else if (ownsDisabled) {
              attrs.$set('disabled', ok ? undefined : 'disabled');
            }
            element.toggleClass('bm-requires-denied', !ok);
            attrs.$set('aria-disabled', ok ? undefined : 'true');
          });

          // Sin `disabled` real, el click (mouse o teclado) se frena acá.
          const blockClick = function (event) {
            if (allowed()) return;
            event.preventDefault();
            event.stopImmediatePropagation();
          };
          node.addEventListener('click', blockClick, true);
          scope.$on('$destroy', function () { node.removeEventListener('click', blockClick, true); });
        };
      }
    };
  }
]);
